/**
 * What the in-app device-approval banner should say for THIS install.
 *
 * Approval is an app-owned trust list (Manage devices), not a sign-in or call
 * gate: a pending device can message and call normally; the only thing it
 * cannot do is approve other devices. The banner exists so a new install's
 * pending state is never a silent surprise, and so the trusted side sees the
 * request promptly.
 *
 * Only an ACTIVE approver (trusted, used within ACTIVE_APPROVER_WINDOW_DAYS)
 * can approve; idle trusted devices are never named as approvers. With no
 * active approver but some idle ones, this install can verify by email.
 */
import { ACTIVE_APPROVER_WINDOW_DAYS } from '@shared/devices/types'

export { ACTIVE_APPROVER_WINDOW_DAYS }

const DAY_MS = 24 * 60 * 60 * 1000

type ApprovalDevice = {
  deviceId: string
  displayName: string
  status: 'pending' | 'trusted' | 'revoked'
  lastSeenAt: string
}

export type DeviceApprovalSummary<D extends ApprovalDevice = ApprovalDevice> =
  | { kind: 'none' }
  | {
      kind: 'this-pending'
      thisDevice: D
      /** Trusted devices used in the last 14 days: the only ones that can approve. */
      approvers: D[]
      /** Trusted devices idle for 14+ days (they can't approve). */
      idleTrusted: D[]
    }
  | { kind: 'others-pending'; pending: D[] }

/** Whole days since `lastSeenAt` (0 for today / unparseable-in-future). */
export function inactiveDays(lastSeenAt: string, nowMs: number = Date.now()): number {
  const seen = Date.parse(lastSeenAt)
  if (!Number.isFinite(seen)) return ACTIVE_APPROVER_WINDOW_DAYS
  return Math.max(0, Math.floor((nowMs - seen) / DAY_MS))
}

/** Not used for ACTIVE_APPROVER_WINDOW_DAYS (the server's approver window). */
export function isIdleDevice(lastSeenAt: string, nowMs: number = Date.now()): boolean {
  const seen = Date.parse(lastSeenAt)
  if (!Number.isFinite(seen)) return true
  return nowMs - seen >= ACTIVE_APPROVER_WINDOW_DAYS * DAY_MS
}

export function summarizeDeviceApproval<D extends ApprovalDevice>(
  devices: readonly D[],
  thisDeviceId: string | null | undefined,
  nowMs: number = Date.now(),
): DeviceApprovalSummary<D> {
  const thisDevice = thisDeviceId ? devices.find((d) => d.deviceId === thisDeviceId) : undefined
  if (!thisDevice) return { kind: 'none' }
  if (thisDevice.status === 'pending') {
    const trusted = devices.filter((d) => d.status === 'trusted' && d.deviceId !== thisDevice.deviceId)
    return {
      kind: 'this-pending',
      thisDevice,
      approvers: trusted.filter((d) => !isIdleDevice(d.lastSeenAt, nowMs)),
      idleTrusted: trusted.filter((d) => isIdleDevice(d.lastSeenAt, nowMs)),
    }
  }
  if (thisDevice.status === 'trusted') {
    const pending = devices.filter((d) => d.status === 'pending' && d.deviceId !== thisDevice.deviceId)
    return pending.length > 0 ? { kind: 'others-pending', pending } : { kind: 'none' }
  }
  return { kind: 'none' }
}

/** Pending, nobody active can approve, but idle trusted devices exist → verify by email. */
export function canVerifyByEmail(summary: DeviceApprovalSummary): boolean {
  return summary.kind === 'this-pending' && summary.approvers.length === 0 && summary.idleTrusted.length > 0
}

/** "Chrome on Mac", "Chrome on Mac or iPhone", "Chrome on Mac, iPad or iPhone". */
export function formatApproverNames(approvers: readonly { displayName: string }[]): string | null {
  const names = [...new Set(approvers.map((d) => d.displayName.trim()).filter(Boolean))]
  if (names.length === 0) return null
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}
